import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type { UserOption } from '@/components/pickers/userOption.ts';
import type { NotificationRecipient } from '@/lib/notificationSend.ts';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import type { Organisation } from '@/types/organisations.ts';
import { sendReach, type SendReach } from './sendReach.ts';
import { useMeetingChoice, type MeetingChoiceState } from './useMeetingChoice.ts';
import {
  useNotificationKind,
  type NotificationKindChoice,
} from './useNotificationKind.ts';
import { useNotificationPreview, type PreviewState } from './useNotificationPreview.ts';
import { useNotificationSend, type NotificationSend } from './useNotificationSend.ts';
import { usePeopleSelection, type PeopleSelection } from './usePeopleSelection.ts';
import { useRecipients, type Recipients, type ScopeKind } from './useRecipients.ts';
import { useSendOptions, type SendOptions } from './useSendOptions.ts';

export interface SendForm {
  kind: NotificationKindChoice;
  options: SendOptions;
  recipients: Recipients;
  selection: PeopleSelection;
  meeting: MeetingChoiceState;
  previewState: PreviewState;
  reach: SendReach;
  sending: NotificationSend;
  canSendList: boolean;
  changeKind: (kind: string) => void;
  changeScope: (scope: ScopeKind) => void;
  changeOrganisation: (organisation: Organisation | null) => void;
  changeCohort: (cohort: CohortWithOrganisation | null) => void;
  chooseChannel: (channel: SendableNotificationChannel | '') => void;
  chooseResend: (resend: boolean) => void;
  chooseRoleplay: (roleplayId: string) => void;
  pickUser: (user: UserOption | null) => void;
  pickFromList: (person: NotificationRecipient) => void;
  clearRecipient: () => void;
  chooseMeeting: (meetingId: string) => void;
}

/** Every choice on the send screen, and what else each choice clears. */
export function useSendForm(headerOrganisation: Organisation | null): SendForm {
  const kind = useNotificationKind();
  const options = useSendOptions(kind);
  const recipients = useRecipients({ headerOrganisation, kind, options });
  const { recipient, people } = recipients;
  const selection = usePeopleSelection(people);
  const meeting = useMeetingChoice(recipient);
  const previewState = useNotificationPreview({
    kind,
    options,
    recipient,
    people,
    selection,
    meetingId: meeting.meetingId,
  });
  const reach = sendReach({
    people,
    selected: selection.selected,
    previewRecipient: previewState.previewRecipient,
    delivery: previewState.preview.data?.delivery,
    isEmail: kind.isEmail,
    channel: options.channel,
    resend: options.resend,
  });
  const sending = useNotificationSend({
    kind,
    options,
    recipient,
    meetingId: meeting.meetingId,
    selectedIds: selection.selectedIds,
    audience: recipients.audience,
    onBulkSent: () => selection.select([]),
  });

  const clearOutcome = () => {
    sending.clearBulkResult();
    sending.clearLastSent();
  };

  /* Another moment is another question, so nothing picked for the previous one carries over. */
  const changeKind = (next: string) => {
    kind.choose(next);
    recipients.clearPicks();
    meeting.clear();
    selection.clear();
    clearOutcome();
    options.reset();
  };

  const startListOver = () => {
    selection.clear();
    meeting.clear();
    options.chooseRoleplay('');
  };

  /* For a user moment, one name picked by hand is a list of one, ticked and ready to send. */
  const pickUser = (user: UserOption | null) => {
    recipients.pickUser(user);
    selection.select(user ? [user.id] : []);
    sending.clearLastSent();
    options.chooseRoleplay('');
  };

  const pickFromList = (person: NotificationRecipient) => {
    recipients.pickFromList(person);
    meeting.clear();
    sending.clearLastSent();
  };

  const clearRecipient = () => {
    recipients.clearPicks();
    meeting.clear();
    sending.clearLastSent();
  };

  return {
    kind,
    options,
    recipients,
    selection,
    meeting,
    previewState,
    reach,
    sending,
    canSendList:
      reach.reachableCount > 0 &&
      !(options.takesNeverName && options.neverNameError !== '') &&
      !(options.takesRoleplay && options.roleplayId === undefined),
    changeKind,
    changeScope: (scope) => {
      recipients.changeScope(scope);
      startListOver();
    },
    changeOrganisation: (organisation) => {
      recipients.changeOrganisation(organisation);
      startListOver();
    },
    changeCohort: (cohort) => {
      recipients.changeCohort(cohort);
      startListOver();
    },
    chooseChannel: (channel) => {
      options.chooseChannel(channel);
      clearOutcome();
    },
    chooseResend: (resend) => {
      options.chooseResend(resend);
      clearOutcome();
    },
    chooseRoleplay: (roleplayId) => {
      options.chooseRoleplay(roleplayId);
      sending.clearBulkResult();
    },
    pickUser,
    pickFromList,
    clearRecipient,
    chooseMeeting: (meetingId) => {
      meeting.choose(meetingId);
      sending.clearLastSent();
    },
  };
}
